import { z } from 'zod';

export enum SectionType {
  FirstParty = 'First Party',
  ThirdParty = 'Third Party',
  Location = 'Location',
  MediaType = 'Media Type',
}

export const ColumnsSchema = z.array(z.string()).min(1, { message: 'Please add at least one column' });

export const SectionSchema = z.object({
  id: z.number(),
  sectionName: z.string().min(1, { message: 'Please enter a name.' }),
  sectionType: z.nativeEnum(SectionType),
  partitionByIo: z.boolean(),
  includeSubtotals: z.boolean(),
  columns: ColumnsSchema,
  columnLabels: z.record(z.string(), z.string().nullable()).nullable(),
  sortBy: z.array(z.string()).nullable(),
  sortAscending: z.array(z.boolean()).nullable(),
});

export const SheetNameSchema = z
  .string()
  .min(1, { message: 'Please enter a name.' })
  .max(31, { message: 'Please keep the name 31 characters or less.' });

const SheetSchema = z.object({
  // The uiId is purely for front-end purposes to uniquely identify each sheet.
  // It should be set to undefined before sending it to the server
  id: z.number().nullable(),
  sheetName: SheetNameSchema,
  reportSections: z.array(SectionSchema).min(1, { message: 'Please add at least one section.' }).max(6),
});

// The lookback `date_part` accepted by the Report Hub backend — see ADOPS-2553.
// Note: `Day`, `Week`, `Month` are capitalized but `month_to_date` is snake_case
// (intentional, matches the backend contract).
export const LookbackDatePartSchema = z.enum(['Day', 'Week', 'Month', 'month_to_date']);
export type LookbackDatePart = z.infer<typeof LookbackDatePartSchema>;

export const LookbackSchema = z.object({
  datePart: LookbackDatePartSchema,
  // length is unused for `month_to_date` but always present so the shape is stable.
  length: z.number().int().min(0),
});

// dateRange is stored in the form as JS Date objects (matching DatePicker.tsx).
// The mapper converts them to 'yyyy-MM-dd' strings before calling the API.
export const DateFilteringSchema = z.object({
  enabled: z.boolean(),
  dateRange: z
    .tuple([z.date().nullable(), z.date().nullable()])
    .nullable()
    .optional(),
  lookback: LookbackSchema.nullable().optional(),
});
export type DateFiltering = z.infer<typeof DateFilteringSchema>;

const ReportTemplateSchema = z.object({
  templateId: z.number().nullable(),
  userId: z.number(),
  reportName: z.string().min(1, { message: 'Please enter a name.' }),
  includeLogo: z.boolean(),
  includeHeader: z.boolean(),
  includeFooter: z.boolean(),
  includeGlossary: z.boolean(),
  venturaContractIds: z.array(z.number()),
  sheetObjects: z.array(SheetSchema).min(1, { message: 'Please add at least one sheet.' }),
  dateFiltering: DateFilteringSchema,
});

export type ReportTemplate = z.infer<typeof ReportTemplateSchema>;

export type Sheet = z.infer<typeof SheetSchema>;

export type Section = z.infer<typeof SectionSchema>;

export const newReportTemplate: ReportTemplate = {
  templateId: null,
  userId: null,
  reportName: 'My New Template',
  includeLogo: false,
  includeHeader: false,
  includeFooter: false,
  includeGlossary: false,
  venturaContractIds: [],
  sheetObjects: [
    {
      sheetName: 'Untitled Sheet 1',
    },
  ],
  dateFiltering: {
    enabled: false,
  },
};

export const newSection: Section = {
  sectionName: '',
  sectionType: SectionType.FirstParty,
  partitionByIo: false,
  includeSubtotals: false,
  columns: [],
  columnLabels: null,
  sortBy: null,
  sortAscending: null,
};

export default ReportTemplateSchema;
